import { ON_LOGOUT } from 'app/actions/authActions';
import { createReducer, getError } from 'utils/appHelpers';
import { API, graphqlOperation } from 'aws-amplify';
import {
  createProjectMutation,
  deleteProjectMutation,
  updateLikieeProjects,
  updateProjectMutation,
} from 'graphql/projects/mutations';
import { listProjects } from 'graphql/projects/queries';
import store from 'redux/store';
import { onSubscribe } from 'shared/utils/graphHelpers';
import { onCreateProject, onDeleteProject, onUpdateProject } from 'graphql/projects/subscriptions';
import { getAndUpdateProduct } from './products';
import { isCacheValid, setCacheTimestamp, CACHE_TTL, incrementCacheHit, incrementCacheMiss } from 'utils/cacheHelpers';

const ON_PROJECTS_GET = '@@home/ON_PROJECTS_GET';
const ON_PROJECT_ADD = '@@home/ON_PROJECT_ADD';
const ON_PROJECT_UPDATE = '@@home/ON_PROJECT_UPDATE';
const ON_PROJECT_REMOVE = '@@home/ON_PROJECT_REMOVE';
const ON_PROJECTS_IMAGES_GET = '@@home/ON_PROJECTS_IMAGES_GET';

let projectCreateSubs;
let projectDeleteSubs;
let projectUpdateSubs;

const projectHandlers = {
  [ON_PROJECTS_GET]: (state, action) => action.payload,
  [ON_PROJECT_ADD]: (state, action) => ({ ...state, [action.payload.id]: action.payload }),
  [ON_PROJECT_UPDATE]: (state, action) => ({ ...state, [action.payload.id]: action.payload }),
  [ON_PROJECT_REMOVE]: (state, action) => {
    const newState = { ...state };
    delete newState[action.payload.id];
    return { ...newState };
  },
  [ON_LOGOUT]: () => {
    projectCreateSubs?.unsubscribe();
    projectDeleteSubs?.unsubscribe();
    projectUpdateSubs?.unsubscribe();
    return {};
  },
};

const projectImagesHandlers = {
  [ON_PROJECTS_IMAGES_GET]: (state, action) => action.payload,
  [ON_LOGOUT]: () => [],
};

export const onSetProjects = payload => ({ type: ON_PROJECTS_GET, payload });
export const onSetProjectsImages = payload => ({ type: ON_PROJECTS_IMAGES_GET, payload });

const onAddProject = payload => ({ type: ON_PROJECT_ADD, payload });
const onChangeProject = payload => ({ type: ON_PROJECT_UPDATE, payload });
const onRemoveProject = payload => ({ type: ON_PROJECT_REMOVE, payload });

const subscribeToProjectChanges = async () => {
  projectCreateSubs = await onSubscribe(onCreateProject, 'onCreateProject', project => {
    store.dispatch(onAddProject(project));
  });
  projectDeleteSubs = await onSubscribe(onDeleteProject, 'onDeleteProject', project => {
    store.dispatch(onRemoveProject(project));
  });
  projectUpdateSubs = await onSubscribe(onUpdateProject, 'onUpdateProject', project => {
    store.dispatch(onChangeProject(project));
  });
};

export const getProjects = async (force = false) => {
  try {
    // Check if we have valid cached data
    const state = store.getState();
    const hasData = state.projects && Object.keys(state.projects).length > 0;

    if (!force && hasData && isCacheValid('projects', CACHE_TTL.PROJECTS)) {
      incrementCacheHit();
      subscribeToProjectChanges(); // Ensure subscriptions are active
      return; // Use cached data
    }

    incrementCacheMiss();

    const body = { limit: 1000 };
    const { data } = await API.graphql(graphqlOperation(listProjects, body));
    const result = data.listProjects.items;
    const projects = {};
    result.forEach(project => {
      projects[project.id] = project;
    });
    store.dispatch(onSetProjects(projects));
    setCacheTimestamp('projects'); // Mark cache as fresh
    subscribeToProjectChanges();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
    store.dispatch(onSetProjects({}));
  }
};

// Add force refresh function
export const refreshProjects = () => getProjects(true);

export const createProject = async body => {
  const { data } = await API.graphql(graphqlOperation(createProjectMutation, { input: body }));
  await store.dispatch(onAddProject(data.createProject));
  // Invalidate cache when data changes
  setCacheTimestamp('projects');
};

export const updateProject = async body => {
  const input = { ...body };
  const { data } = await API.graphql(graphqlOperation(updateProjectMutation, { input }));
  await store.dispatch(onChangeProject(data.updateProject));
  // Update cache timestamp
  setCacheTimestamp('projects');
};

export const deleteProject = async body => {
  const input = { id: body.id };
  const { data } = await API.graphql(graphqlOperation(deleteProjectMutation, { input }));
  await store.dispatch(onRemoveProject(data.deleteProject.id));
  // Update cache timestamp
  setCacheTimestamp('projects');
};

export const toggleProjectFromLikiee = async (likieeID, projectIDs) => {
  try {
    const body = { projectIDs, likieeID };
    await API.graphql(graphqlOperation(updateLikieeProjects, body));
    getAndUpdateProduct(likieeID);
  } catch (err) {
    throw new Error(getError(err));
  }
};

export const projectReducer = createReducer(null, projectHandlers);
export const projectImagesReducer = createReducer([], projectImagesHandlers);
